export interface ProfileFormDefaults {
  oracleId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  dob: Date | undefined;
  gender: string;
  maritalStatus: string;
  email: string;
  emailWork: string;
  mobile: string;
  joiningDate: Date | undefined;
  latestStartDate: Date | undefined;
  permanentAddress: string;
  temporaryAddress: string;
  uan: string;
  aadhar: string;
  pan: string;
  locationCountry: string;
  locationSite: string;
  department: string;
  designation: string;
  employeeUid: string;
  managerFirstName: string;
  managerLastName: string;
  managerEmail: string;
  place: string;
  phone: string;
}

export interface FieldEditability {
  [fieldKey: string]: boolean;
}

// Handles "M/D/YYYY h:mm:ss AM/PM" format from Oracle + standard ISO strings
const parseProfileDate = (
  dateStr: string | undefined | null,
): Date | undefined => {
  if (!dateStr) return undefined;
  const usMatch = dateStr.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)?$/i,
  );
  if (usMatch) {
    const [, month, day, year, hr, min, sec, ampm] = usMatch;
    let hour = parseInt(hr);
    if (ampm?.toUpperCase() === "PM" && hour < 12) hour += 12;
    if (ampm?.toUpperCase() === "AM" && hour === 12) hour = 0;
    const d = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      hour,
      parseInt(min),
      parseInt(sec),
    );
    return isNaN(d.getTime()) ? undefined : d;
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? undefined : d;
};

const extractCity = (locationSite: string): string => {
  if (!locationSite) return "";
  const parts = locationSite.split(",");
  return (parts[parts.length - 1] || "").trim();
};

// Strip country code from phone: "+918019822251" → "8019822251"
const cleanMobile = (phone: string): string => {
  if (!phone) return "";
  return phone.replace(/^\+?91/, "").replace(/\D/g, "");
};

export const mapProfileToDefaults = (
  profileData: any,
): {
  defaults: ProfileFormDefaults;
  editability: FieldEditability;
} => {
  const autoEmp = profileData?.automation?.employee || {};
  const oracleEmp = profileData?.oracle?.employee || {};
  const oracleManager = profileData?.oracle?.manager || {};
  const mobileRaw = profileData?.mobileNumber?.row?.mobile || "";
  const workEmail = profileData?.oracleAyehuDb?.work_email || "";

  console.log("profileFormMapper: autoEmp", autoEmp);
  console.log("profileFormMapper: oracleEmp", oracleEmp);
  console.log("profileFormMapper: mobileRaw", mobileRaw);

  // Gender extraction priority: automation → oracleAyehuDb → oracle
  const gender =
    autoEmp.gender?.trim() ||
    profileData?.oracleAyehuDb?.gender?.trim() ||
    oracleEmp.gender?.trim() ||
    "";

  const defaults: ProfileFormDefaults = {
    oracleId: autoEmp.oracle_id || oracleEmp.oracle_id || "",
    fullName:
      `${autoEmp.first_name || oracleEmp.first_name || ""} ${autoEmp.last_name || oracleEmp.last_name || ""}`.trim(),
    firstName: autoEmp.first_name || oracleEmp.first_name || "",
    lastName: autoEmp.last_name || oracleEmp.last_name || "",
    fatherName: "",
    dob: parseProfileDate(oracleEmp.date_of_birth),
    gender: gender,
    maritalStatus: "",
    email: oracleEmp.employee_email || autoEmp.email || "",
    emailWork: workEmail || autoEmp.email_work || "",
    mobile:
      cleanMobile(mobileRaw) ||
      cleanMobile(autoEmp.phone || "") ||
      oracleEmp.employee_mobile ||
      "",
    joiningDate: parseProfileDate(
      autoEmp.latest_start_date || oracleEmp.latest_start_dt,
    ),
    latestStartDate: parseProfileDate(autoEmp.latest_start_date),
    permanentAddress: "",
    temporaryAddress: "",
    uan: "",
    aadhar: "",
    pan: "",
    locationCountry:
      autoEmp.location_country || oracleEmp.location_country || "",
    locationSite: oracleEmp.location_site || autoEmp.location_site || "",
    department: oracleEmp.department || "",
    designation:
      oracleEmp.designation || oracleEmp.job_title || autoEmp.job_title || "",
    employeeUid: autoEmp.employee_uid || autoEmp.employee_IID || "",
    managerFirstName: oracleManager.manager_first || "",
    managerLastName: oracleManager.manager_last || "",
    managerEmail: oracleManager.manager_email || "",
    place:
      extractCity(oracleEmp.location_site || "") ||
      oracleEmp.town_or_city ||
      "",
    phone: autoEmp.phone || oracleEmp.employee_mobile || "",
  };

  const editability: FieldEditability = {
    oracleId: !!defaults.oracleId,
    fullName: !!defaults.fullName,
    dob: !!defaults.dob,
    gender: !!defaults.gender,
    maritalStatus: !!defaults.maritalStatus,
    email: !!defaults.email,
    mobile: !!defaults.mobile,
    joiningDate: !!defaults.joiningDate,
    place: !!defaults.place,
    department: !!defaults.department,
    designation: !!defaults.designation,
    fatherName: false,
    permanentAddress: false,
    temporaryAddress: false,
    uan: false,
    aadhar: false,
    pan: false,
  };

  return { defaults, editability };
};

// ── Helper: derive gender from profileData (called in MainPage) ──────────────
// Returns true if employee is female — used to conditionally show Form L tab
export const isFemaleEmployee = (profileData: any): boolean => {
  const gender =
    profileData?.automation?.employee?.gender?.trim() ||
    profileData?.oracleAyehuDb?.gender?.trim() ||
    profileData?.oracle?.employee?.gender?.trim() ||
    "";
  return gender.toLowerCase() === "female" || gender.toLowerCase() === "f";
};

// ── Form-specific initial data builders ──────────────────────────────────────

export const buildForm2InitialData = (d: ProfileFormDefaults) => ({
  oracleId: d.oracleId,
  fullName: d.fullName,
  dob: d.dob,
  gender: d.gender || undefined,
  maritalStatus: d.maritalStatus || undefined,
  joiningDate: d.joiningDate,
  permanentAddress: d.permanentAddress,
  temporaryAddress: d.temporaryAddress,
  uan: d.uan,
  place: d.place,
  date: d.joiningDate,
  TotalAmount: "100",
  employerSignature: "",
});

export const buildGratuityInitialData = (d: ProfileFormDefaults) => ({
  toName: "TTEC INDIA CUSTOMER SOLUTIONS PVT. LTD.",
  address: d.locationSite || "",
  employeeName: d.fullName,
  gender: d.gender || undefined,
  maritalStatus: d.maritalStatus || undefined,
  permanentAddress: d.permanentAddress,
  department: "Undergoing Training",
  postHeld: d.oracleId,
  appointmentDate: d.joiningDate,
  place: d.place,
  date: d.joiningDate,
  finalDate: d.joiningDate,
  joiningLocation: d.place || d.locationSite,
  establishmentAddress: d.locationSite ? `TTEC, ${d.locationSite}` : "",
  designation: "",
  religion: "NA",
  employerSignature: "",
  subscriberSignature: "",
  proportion: "100",
});

export const buildForm11InitialData = (d: ProfileFormDefaults) => ({
  memberName: d.fullName,
  fatherName: d.fatherName,
  dob: d.dob,
  gender: d.gender || undefined,
  maritalStatus: d.maritalStatus || undefined,
  email: d.email,
  mobile: d.mobile,
  uan: d.uan,
  place: d.place,
  date: d.joiningDate,
  memberNameConfirm: d.fullName,
  joinedOn: d.joiningDate,
  signatureDate: d.joiningDate,
  employerSignature: "",
  internationalWorker: "no",
});

// ── NEW: Form L initial data (Female India employees only) ───────────────────
export const buildFormLInitialData = (d: ProfileFormDefaults) => ({
  fullName: d.fullName,
  // Full address: use permanentAddress if available, else locationSite
  fullAddress: d.permanentAddress || d.locationSite || "",
  designation: d.designation || "",
  dateOfJoining: d.joiningDate,
  // Working location: extract readable city/site from locationSite
  workingLocation: d.locationSite || d.place || "",
});
